import { useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react"
import { Box, Film, Loader2, MapPin, Package, RefreshCw, Scissors, Users } from "lucide-react"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { EntityThumbSquare } from "@/components/workspace/entity-thumb-square"
import { useTranslation } from "@/hooks/use-translation"
import { SCALE_SHEET_CANVAS_BG } from "@/lib/scale-sheet-layout"
import { cn } from "@/lib/utils"

export type RefCharacter = {
  name: string
  imageUrl: string | null
  downloadUrl?: string | null
  subtitle?: string | null
  /** Лист, раскладка или образ героя обновились после последней генерации шота. */
  appearanceUpdated?: boolean
}

export type RefLocation = {
  name: string
  subtitle?: string | null
  imageUrl: string | null
  downloadUrl?: string | null
}

export type RefProp = {
  name: string
  subtitle?: string | null
  imageUrl: string | null
  downloadUrl?: string | null
  /** Silver padded canvas chrome — only for prop front-view figures. */
  frontViewChrome?: boolean
  /** Раскладка, вид спереди или состояние предмета обновились после последней генерации шота. */
  appearanceUpdated?: boolean
}

export type RefVideoFrame = {
  url: string
  label: string
}

export type RefScene3d = {
  name: string
  imageUrl: string | null
}

/** Референс кадра из API (shot_node.reference_images): персонаж, локация или реквизит. */
export type ReferenceImage = {
  type: string
  name: string
  subtitle?: string | null
  url: string | null
  download_url?: string | null
  front_view_chrome?: boolean
  /** Для героя и реквизита: картинка, которая уходит в генерацию, обновлена после последней генерации шота. */
  appearance_updated?: boolean
}

type Props = {
  projectId: number
  characters: RefCharacter[]
  props?: RefProp[]
  location: RefLocation | null
  scene3d?: RefScene3d | null
  videoFrames?: RefVideoFrame[]
  onExtractFrames?: () => void
  extracting?: boolean
  extractDisabled?: boolean
  children?: ReactNode
  className?: string
  /** stack: узкая колонка — секции и герои друг под другом (студия). */
  layout?: "grid" | "stack"
  onOpenCharacters?: () => void
  onOpenProps?: () => void
  onOpenLocation?: () => void
  onOpenScene3d?: () => void
}

function sectionClickProps(onOpen?: () => void) {
  if (!onOpen) return { handlers: {}, className: "" as string }
  return {
    handlers: {
      role: "button" as const,
      tabIndex: 0,
      onClick: onOpen,
      onKeyDown: (e: ReactKeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onOpen()
        }
      },
    },
    className: "cursor-pointer",
  }
}

function SectionHeading({
  icon: Icon,
  label,
  onOpen,
  openTitle,
}: {
  icon: typeof Users
  label: string
  onOpen?: () => void
  openTitle?: string
}) {
  const clickProps = sectionClickProps(onOpen)
  return (
    <div
      className={cn(
        "mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase",
        clickProps.className,
        onOpen && "hover:text-ui-text-secondary",
      )}
      title={openTitle}
      {...clickProps.handlers}
    >
      <Icon className="h-3 w-3" /> {label}
    </div>
  )
}

function RefThumb({
  projectId,
  url,
  downloadUrl,
  name,
  onLightboxToggle,
  openOriginalTitle,
}: {
  projectId: number
  url: string
  downloadUrl?: string | null
  name: string
  onLightboxToggle?: (open: boolean) => void
  openOriginalTitle: string
}) {
  const [open, setOpen] = useState(false)
  const sourceUrl = downloadUrl ?? url
  const handleChange = (next: boolean) => {
    setOpen(next)
    onLightboxToggle?.(next)
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          handleChange(true)
        }}
        className="shrink-0 cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
        title={openOriginalTitle}
      >
        <EntityThumbSquare name={name} imageUrl={url} size="compact" />
      </button>
      <SketchDialog
        open={open}
        onOpenChange={handleChange}
        projectId={projectId}
        sourceUrl={sourceUrl}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={name}
      />
    </>
  )
}

function CharCard({
  projectId,
  ch,
  onLightboxToggle,
  openOriginalTitle,
}: {
  projectId: number
  ch: RefCharacter
  onLightboxToggle?: (open: boolean) => void
  openOriginalTitle: string
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5">
      {ch.imageUrl ? (
        <RefThumb
          projectId={projectId}
          url={ch.imageUrl}
          downloadUrl={ch.downloadUrl}
          name={ch.name}
          onLightboxToggle={onLightboxToggle}
          openOriginalTitle={openOriginalTitle}
        />
      ) : (
        <EntityThumbSquare name={ch.name} size="compact" />
      )}
      <div className="min-w-0">
        <span className="block truncate text-xs leading-tight font-medium text-ui-text-primary">{ch.name}</span>
        {ch.subtitle && (
          <span className="block truncate text-[11px] leading-tight text-ui-text-muted">{ch.subtitle}</span>
        )}
      </div>
      {ch.appearanceUpdated ? <AppearanceUpdatedMark kind="character" /> : null}
    </div>
  )
}

/** Герой или предмет получил новую картинку для генерации после последней генерации шота. */
function AppearanceUpdatedMark({ kind }: { kind: "character" | "prop" }) {
  const { t } = useTranslation("shot")
  const label = t(kind === "prop" ? "references.propAppearanceUpdated" : "references.appearanceUpdated")
  return (
    <ActionHintTooltip label={label}>
      <span
        role="img"
        aria-label={label}
        className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-amber-400"
      >
        <RefreshCw className="h-3 w-3" aria-hidden />
      </span>
    </ActionHintTooltip>
  )
}

function LocCard({
  projectId,
  loc,
  onLightboxToggle,
  openOriginalTitle,
}: {
  projectId: number
  loc: RefLocation
  onLightboxToggle?: (open: boolean) => void
  openOriginalTitle: string
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5">
      {loc.imageUrl ? (
        <RefThumb
          projectId={projectId}
          url={loc.imageUrl}
          downloadUrl={loc.downloadUrl}
          name={loc.name}
          onLightboxToggle={onLightboxToggle}
          openOriginalTitle={openOriginalTitle}
        />
      ) : (
        <EntityThumbSquare name={loc.name} size="compact" />
      )}
      <div className="min-w-0">
        <span className="block truncate text-xs leading-tight font-medium text-ui-text-primary">{loc.name}</span>
        {loc.subtitle && (
          <span className="block truncate text-[11px] leading-tight text-ui-text-muted">{loc.subtitle}</span>
        )}
      </div>
    </div>
  )
}

function PropThumb({
  projectId,
  url,
  downloadUrl,
  name,
  frontViewChrome = false,
  onLightboxToggle,
  openOriginalTitle,
}: {
  projectId: number
  url: string
  downloadUrl?: string | null
  name: string
  frontViewChrome?: boolean
  onLightboxToggle?: (open: boolean) => void
  openOriginalTitle: string
}) {
  const [open, setOpen] = useState(false)
  const sourceUrl = downloadUrl ?? url
  const handleChange = (next: boolean) => {
    setOpen(next)
    onLightboxToggle?.(next)
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          handleChange(true)
        }}
        className="shrink-0 cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
        title={openOriginalTitle}
      >
        <EntityThumbSquare
          name={name}
          imageUrl={url}
          size="compact"
          imageFit={frontViewChrome ? "contain" : "cover"}
          imagePadded={frontViewChrome}
          imageBackgroundColor={frontViewChrome ? SCALE_SHEET_CANVAS_BG : undefined}
        />
      </button>
      <SketchDialog
        open={open}
        onOpenChange={handleChange}
        projectId={projectId}
        sourceUrl={sourceUrl}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={name}
      />
    </>
  )
}

function PropCard({
  projectId,
  prop,
  onLightboxToggle,
  openOriginalTitle,
}: {
  projectId: number
  prop: RefProp
  onLightboxToggle?: (open: boolean) => void
  openOriginalTitle: string
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5">
      {prop.imageUrl ? (
        <PropThumb
          projectId={projectId}
          url={prop.imageUrl}
          downloadUrl={prop.downloadUrl}
          name={prop.name}
          frontViewChrome={prop.frontViewChrome}
          onLightboxToggle={onLightboxToggle}
          openOriginalTitle={openOriginalTitle}
        />
      ) : (
        <EntityThumbSquare name={prop.name} size="compact" />
      )}
      <div className="min-w-0">
        <span className="block truncate text-xs leading-tight font-medium text-ui-text-primary">{prop.name}</span>
        {prop.subtitle && (
          <span className="block truncate text-[11px] leading-tight text-ui-text-muted">{prop.subtitle}</span>
        )}
      </div>
      {prop.appearanceUpdated ? <AppearanceUpdatedMark kind="prop" /> : null}
    </div>
  )
}

function stripHint(name: string, kind: string, subtitle?: string | null) {
  const parts = [name, kind, subtitle].filter((part): part is string => Boolean(part))
  return parts.join(" · ")
}

function StripThumb({
  projectId,
  name,
  kind,
  subtitle,
  imageUrl,
  downloadUrl,
  frontViewChrome = false,
  onLightboxToggle,
}: {
  projectId: number
  name: string
  kind: string
  subtitle?: string | null
  imageUrl?: string | null
  downloadUrl?: string | null
  frontViewChrome?: boolean
  onLightboxToggle?: (open: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const url = imageUrl ?? null
  const sourceUrl = downloadUrl ?? url
  const handleChange = (next: boolean) => {
    setOpen(next)
    onLightboxToggle?.(next)
  }
  const thumb = (
    <EntityThumbSquare
      name={name}
      imageUrl={url}
      size="compact"
      imageFit={frontViewChrome ? "contain" : "cover"}
      imagePadded={frontViewChrome}
      imageBackgroundColor={frontViewChrome ? SCALE_SHEET_CANVAS_BG : undefined}
    />
  )
  const hint = stripHint(name, kind, subtitle)

  if (!url) {
    return (
      <ActionHintTooltip label={hint}>
        <span className="inline-flex shrink-0">{thumb}</span>
      </ActionHintTooltip>
    )
  }

  return (
    <>
      <ActionHintTooltip label={hint}>
        <button
          type="button"
          onClick={() => handleChange(true)}
          className="shrink-0 cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
        >
          {thumb}
        </button>
      </ActionHintTooltip>
      <SketchDialog
        open={open}
        onOpenChange={handleChange}
        projectId={projectId}
        sourceUrl={sourceUrl}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={name}
      />
    </>
  )
}

export function ShotReferencesPanel({
  projectId,
  characters,
  props = [],
  location,
  scene3d = null,
  videoFrames = [],
  onExtractFrames,
  extracting = false,
  extractDisabled = false,
  children,
  className,
  layout = "grid",
  onOpenCharacters,
  onOpenProps,
  onOpenLocation,
  onOpenScene3d,
}: Props) {
  const { t } = useTranslation("shot")

  // Просмотр картинки в модалке не должен переключать левую панель: закрытие
  // Radix-диалога порождает «проваливающийся» клик по секции под ним. Гасим клик
  // по секции, пока модалка открыта и короткое время после её закрытия.
  const lightboxOpenRef = useRef(false)
  const lightboxClosedAtRef = useRef(0)
  const handleLightboxToggle = (open: boolean) => {
    lightboxOpenRef.current = open
    if (!open) lightboxClosedAtRef.current = Date.now()
  }
  const guardedOpen = (onOpen?: () => void) => {
    if (!onOpen) return undefined
    return () => {
      if (lightboxOpenRef.current) return
      if (Date.now() - lightboxClosedAtRef.current < 350) return
      onOpen()
    }
  }

  if (layout === "stack") {
    const sections: ReactNode[] = []
    const charListClass = "flex flex-col gap-2"

    if (characters.length > 0) {
      const clickProps = sectionClickProps(guardedOpen(onOpenCharacters))
      sections.push(
        <div
          key="chars"
          className={cn("w-fit max-w-full min-w-0", clickProps.className)}
          {...clickProps.handlers}
          title={onOpenCharacters ? t("references.openCharacters") : undefined}
        >
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
            <Users className="h-3 w-3" /> {t("references.characters")}
          </div>
          <div className={charListClass}>
            {characters.map((ch, i) => (
              <CharCard
                key={i}
                projectId={projectId}
                ch={ch}
                onLightboxToggle={handleLightboxToggle}
                openOriginalTitle={t("references.openOriginal")}
              />
            ))}
          </div>
        </div>,
      )
    }
    if (props.length > 0) {
      const clickProps = sectionClickProps(guardedOpen(onOpenProps))
      sections.push(
        <div
          key="props"
          className={cn("w-fit max-w-full min-w-0", clickProps.className)}
          {...clickProps.handlers}
          title={onOpenProps ? t("references.openProps") : undefined}
        >
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
            <Package className="h-3 w-3" /> {t("references.props")}
          </div>
          <div className={charListClass}>
            {props.map((p, i) => (
              <PropCard
                key={i}
                projectId={projectId}
                prop={p}
                onLightboxToggle={handleLightboxToggle}
                openOriginalTitle={t("references.openOriginal")}
              />
            ))}
          </div>
        </div>,
      )
    }
    if (children) sections.push(children)
    if (location) {
      const clickProps = sectionClickProps(guardedOpen(onOpenLocation))
      sections.push(
        <div
          key="loc"
          className={cn("w-fit max-w-full min-w-0", clickProps.className)}
          {...clickProps.handlers}
          title={onOpenLocation ? t("references.openLocation") : undefined}
        >
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
            <MapPin className="h-3 w-3" /> {t("references.location")}
          </div>
          <LocCard
            projectId={projectId}
            loc={location}
            onLightboxToggle={handleLightboxToggle}
            openOriginalTitle={t("references.openOriginal")}
          />
        </div>,
      )
    }
    if (scene3d) {
      const clickProps = sectionClickProps(guardedOpen(onOpenScene3d))
      sections.push(
        <div
          key="scene3d"
          className={cn("w-fit max-w-full min-w-0", clickProps.className)}
          {...clickProps.handlers}
          title={onOpenScene3d ? t("references.openScene3d") : undefined}
        >
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
            <Box className="h-3 w-3" /> {t("references.scene3d")}
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5">
            <EntityThumbSquare name={scene3d.name} imageUrl={scene3d.imageUrl} size="compact" />
            <span className="block min-w-0 truncate text-xs leading-tight font-medium text-ui-text-primary">
              {scene3d.name}
            </span>
          </div>
        </div>,
      )
    }

    if (sections.length === 0) return null
    return <div className={cn("flex flex-col gap-3", className)}>{sections}</div>
  }

  const showExtract = Boolean(onExtractFrames) && videoFrames.length === 0
  const hasStrip =
    characters.length > 0 ||
    props.length > 0 ||
    location != null ||
    scene3d != null ||
    videoFrames.length > 0 ||
    showExtract ||
    Boolean(children)
  if (!hasStrip) return null

  return (
    <div className={cn("flex flex-wrap items-start gap-x-6 gap-y-3", className)}>
      {characters.length > 0 ? (
        <div className="min-w-0">
          <SectionHeading
            icon={Users}
            label={t("references.characters")}
            onOpen={onOpenCharacters}
            openTitle={onOpenCharacters ? t("references.openCharacters") : undefined}
          />
          <div className="flex flex-wrap items-center gap-2">
            {characters.map((ch, i) => (
              <StripThumb
                key={`char-${i}`}
                projectId={projectId}
                name={ch.name}
                kind={t("references.kindCharacter")}
                subtitle={ch.subtitle}
                imageUrl={ch.imageUrl}
                downloadUrl={ch.downloadUrl}
                onLightboxToggle={handleLightboxToggle}
              />
            ))}
          </div>
        </div>
      ) : null}
      {props.length > 0 ? (
        <div className="min-w-0">
          <SectionHeading
            icon={Package}
            label={t("references.props")}
            onOpen={onOpenProps}
            openTitle={onOpenProps ? t("references.openProps") : undefined}
          />
          <div className="flex flex-wrap items-center gap-2">
            {props.map((p, i) => (
              <StripThumb
                key={`prop-${i}`}
                projectId={projectId}
                name={p.name}
                kind={t("references.kindProp")}
                subtitle={p.subtitle}
                imageUrl={p.imageUrl}
                downloadUrl={p.downloadUrl}
                frontViewChrome={p.frontViewChrome}
                onLightboxToggle={handleLightboxToggle}
              />
            ))}
          </div>
        </div>
      ) : null}
      {location ? (
        <div className="min-w-0">
          <SectionHeading
            icon={MapPin}
            label={t("references.location")}
            onOpen={onOpenLocation}
            openTitle={onOpenLocation ? t("references.openLocation") : undefined}
          />
          <div className="flex flex-wrap items-center gap-2">
            <StripThumb
              projectId={projectId}
              name={location.name}
              kind={t("references.kindLocation")}
              subtitle={location.subtitle}
              imageUrl={location.imageUrl}
              downloadUrl={location.downloadUrl}
              onLightboxToggle={handleLightboxToggle}
            />
          </div>
        </div>
      ) : null}
      {scene3d ? (
        <div className="min-w-0">
          <SectionHeading
            icon={Box}
            label={t("references.scene3d")}
            onOpen={onOpenScene3d}
            openTitle={onOpenScene3d ? t("references.openScene3d") : undefined}
          />
          <div className="flex flex-wrap items-center gap-2">
            <ActionHintTooltip label={scene3d.name}>
              <button
                type="button"
                onClick={() => onOpenScene3d?.()}
                className="shrink-0 cursor-pointer rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
                aria-label={t("references.openScene3d")}
              >
                <EntityThumbSquare name={scene3d.name} imageUrl={scene3d.imageUrl} size="compact" />
              </button>
            </ActionHintTooltip>
          </div>
        </div>
      ) : null}
      {videoFrames.length > 0 || showExtract ? (
        <div className="min-w-0">
          <SectionHeading icon={Film} label={t("common.videoFrames")} />
          <div className="flex flex-wrap items-center gap-2">
            {videoFrames.map((frame, i) => (
              <StripThumb
                key={`frame-${i}`}
                projectId={projectId}
                name={frame.label}
                kind=""
                imageUrl={frame.url}
                onLightboxToggle={handleLightboxToggle}
              />
            ))}
            {showExtract ? (
              <ActionHintTooltip label={t("common.extractFrames")}>
                <button
                  type="button"
                  disabled={extractDisabled || extracting}
                  onClick={onExtractFrames}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-ui-border bg-ui-surface-sunken text-ui-text-muted hover:text-ui-text-primary disabled:opacity-40"
                  aria-label={t("common.extractFrames")}
                >
                  {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />}
                </button>
              </ActionHintTooltip>
            ) : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  )
}
